import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { ImperialMetricDisplayPreference } from "@WahooFitness/cloud-client-types";
import { t } from "@lingui/macro";
import { convertKilometersToMiles, convertMilesToKilometers } from "@/services/convertUnits";
import { Responses } from "@/components/Quiz/useQuiz";

interface Props {
  initialVal?: number | "skip"; // in km (server unit)
  min: number; // in km
  max: number; // in km
  userUnitPreference: ImperialMetricDisplayPreference;
  setUserUnitPreference: (unit: ImperialMetricDisplayPreference) => void;
  setCurrentStepHasValue: (val: boolean) => void;
  setCurrentStepValid: (val: boolean) => void;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
}

function useNumericDistanceEntry({
  initialVal,
  min,
  max,
  userUnitPreference,
  setUserUnitPreference,
  setCurrentStepHasValue,
  setCurrentStepValid,
  setResponses,
  questionId,
}: Props) {
  const isImperial = userUnitPreference === ImperialMetricDisplayPreference.imperial;

  const toDisplayVal = useCallback(
    (km: number) => (isImperial ? convertKilometersToMiles(km) : km),
    [isImperial]
  );

  const toKm = useCallback(
    (display: number) => (isImperial ? convertMilesToKilometers(display) : display),
    [isImperial]
  );

  const displayMin = isImperial ? convertKilometersToMiles(min) : min;
  const displayMax = isImperial ? convertKilometersToMiles(max) : max;

  const [val, setVal] = useState<string>(
    initialVal !== undefined && initialVal !== "skip"
      ? String(Math.round(toDisplayVal(initialVal)))
      : ""
  );
  const [skipChecked, setSkipChecked] = useState(initialVal === "skip");
  const [error, setError] = useState("");

  const checkIsValid = useCallback(
    (display: string) => {
      const num = +display;
      if (display.trim() === "" || Number.isNaN(num)) {
        setCurrentStepHasValue(false);
        setError("");
        return;
      }
      setCurrentStepHasValue(true);
      if (num >= displayMin && num <= displayMax) {
        setCurrentStepValid(true);
        setError("");
      } else {
        setCurrentStepValid(false);
        setError(t`Value must be between ${Math.round(displayMin)} and ${Math.round(displayMax)}`);
      }
    },
    [displayMin, displayMax, setCurrentStepHasValue, setCurrentStepValid]
  );

  useEffect(() => {
    if (!skipChecked) {
      checkIsValid(val);
    } else {
      setCurrentStepValid(true);
      setCurrentStepHasValue(true);
    }
  }, [
    displayMin,
    displayMax,
    skipChecked,
    checkIsValid,
    val,
    setCurrentStepValid,
    setCurrentStepHasValue,
  ]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      setVal(raw);
      const num = +raw;
      setResponses((prev) => {
        if (prev) prev[questionId] = !Number.isNaN(num) && raw.trim() !== "" ? [toKm(num)] : [];
        return prev;
      });
      checkIsValid(raw);
    },
    [questionId, setResponses, toKm, checkIsValid]
  );

  const handleUnitChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: "metric" | "imperial" | null) => {
      if (value === null) {
        return;
      }
      const currentNum = +val;
      if (currentNum) {
        const converted =
          value === "imperial"
            ? convertKilometersToMiles(currentNum)
            : convertMilesToKilometers(currentNum);
        setVal(String(Math.round(converted)));
      }
      setUserUnitPreference(
        value === "imperial"
          ? ImperialMetricDisplayPreference.imperial
          : ImperialMetricDisplayPreference.metric
      );
    },
    [val, setUserUnitPreference]
  );

  const handleSkipToggle = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (ev.target.checked) {
        setSkipChecked(true);
        setCurrentStepValid(true);
        setCurrentStepHasValue(true);
        setResponses((prev) => {
          if (prev) prev[questionId] = ["skip"];
          return prev;
        });
      } else {
        setSkipChecked(false);
        checkIsValid(val);
        setResponses((prev) => {
          const num = +val;
          if (prev) prev[questionId] = !Number.isNaN(num) && val.trim() !== "" ? [toKm(num)] : [];
          return prev;
        });
      }
    },
    [val, questionId, setCurrentStepHasValue, setCurrentStepValid, setResponses, toKm, checkIsValid]
  );

  return {
    val,
    skipChecked,
    error,
    isImperial,
    onChange,
    handleUnitChange,
    handleSkipToggle,
  };
}

export default useNumericDistanceEntry;
